import { Analytics } from '@cobliteam/analytics';
import { type MediaEvent, type MediaView, VideoChannel } from '@cobliteam/common-types';
import { useUpdateFavoriteMedia } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import VideoPlayer from '@cobliteam/ui/components/VideoPlayer';
import { cn } from '@cobliteam/ui/utils';
import { useEffect, useMemo, useState } from 'react';
import { MediaTableType } from '../../../utils/mediaUtils';
import MenuItemDownloadMedia from '../../Menu/MenuItemDownloadMedia';
import MenuItemShareMedia from '../../Menu/MenuItemShareMedia';

const PREFERRED_VIDEOS_ORDER = [VideoChannel.ROAD, VideoChannel.DRIVER];
const EVENT_TRIGGER_SECONDS = 9;

export interface CobliCamPlayerProps {
  mediaView?: MediaView;
  hasPrevious?: boolean;
  hasNext?: boolean;
  canCaptureImage?: boolean;
  crossOrigin?: 'anonymous' | 'use-credentials';
  className?: string;
  origin?: 'Media Details' | 'Recommended Media' | 'Coaching';
  hideControls?: boolean;
  autoPlay?: boolean;
  streamType?: 'live' | 'on-demand';
  liveLabel?: string;
  showLoading?: boolean;
  onPrevious?: (analyticsProperties?: Record<string, string>) => void;
  onNext?: (analyticsProperties?: Record<string, string>) => void;
  onCaptureImage?: (timestamp: number, blobUrl?: string) => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const CobliCamPlayer = ({
  mediaView,
  hasPrevious,
  hasNext,
  canCaptureImage,
  crossOrigin,
  className,
  origin,
  onPrevious,
  onNext,
  onCaptureImage,
  hideControls = false,
  autoPlay,
  streamType,
  liveLabel,
  showLoading = true,
  onFullscreenChange,
}: CobliCamPlayerProps) => {
  const { t } = useTranslation('media');
  const [isLoading, setIsLoading] = useState(true);

  const isEvent = mediaView?.mediaInfo.mediaType === 'EVENT_VIDEO';
  const { mutateAsync: toggleFavoriteMutation } = useUpdateFavoriteMedia(isEvent ? MediaTableType.EVENT_MEDIA : MediaTableType.FOOTAGE_MEDIA);

  const isFavorite = mediaView?.mediaInfo.isFavorite;

  const { medias, isCobliCamMulti } = useMemo(() => {
    // The player requires at least one media.
    if (!mediaView?.videos?.length) {
      return { medias: [], isCobliCamMulti: false };
    }

    const sortedVideos = mediaView.videos
      .filter((video) => video.url) // Filter out videos without valid URLs
      .sort((one, other) => {
        const getChannelIndex = (channel: VideoChannel) => {
          const index = PREFERRED_VIDEOS_ORDER.indexOf(channel);
          return index >= 0 ? index : 1;
        };

        const oneIndex = getChannelIndex(one.channel);
        const otherIndex = getChannelIndex(other.channel);

        return oneIndex - otherIndex;
      })
      .map((video) => {
        const downloadFileName = [mediaView.mediaInfo.mediaType, mediaView.mediaInfo.licensePlate, video.channel].filter(Boolean).join('_').concat('.mp4');

        return {
          src: video.url,
          downloadFileName,
        };
      });

    return {
      medias: sortedVideos,
      isCobliCamMulti: sortedVideos.length > 2,
    };
  }, [mediaView]);

  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    const { mediaType, mediaDetails, isFavorite, severity, id } = mediaView?.mediaInfo || {};
    const eventType = isEvent ? (mediaDetails as MediaEvent).eventTrigger : mediaType;

    Analytics.track(eventName, {
      mediaId: id,
      eventType,
      eventSeverity: severity,
      isFavorite,
      origin,
      ...properties,
    });
  };

  const trackWatched = () => {
    trackEvent('Dashcam Video Watched Fully');
  };

  const trackPlayed = () => {
    trackEvent('Dashcam Video Played');
  };

  const trackDownloaded = () => {
    trackEvent('Media Download Button Clicked');
  };

  const onFavoriteToggle = async () => {
    if (!mediaView) return;

    trackEvent('Favorite Media Button Clicked', { newFavoriteStatus: !isFavorite });

    const data = {
      mediaId: mediaView.mediaInfo.id,
      isFavorite: !isFavorite,
    };

    try {
      toggleFavoriteMutation({ updateUserFavoriteMedia: data });
      Alert.success(isFavorite ? t('player.video_disliked') : t('player.video_liked'));
    } catch {
      Alert.error(isFavorite ? t('player.error_disliking_video') : t('player.error_liking_video'));
    }
  };

  const onCaptureImageError = () => {
    Alert.error(t('player.error_capturing_image'));
    trackEvent('Video Player - Capture Image Error');
  };

  const goToPrevious = onPrevious
    ? () => {
        onPrevious({ component: 'VideoPlayer' });
      }
    : undefined;

  const goToNext = onNext
    ? () => {
        onNext({ component: 'VideoPlayer' });
      }
    : undefined;

  // Reset loading state when medias change
  useEffect(() => {
    setIsLoading(true);
    if (medias.length > 0 && mediaView) {
      // Small delay to ensure player is ready before showing content
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [medias, mediaView]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      onFullscreenChange?.(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [onFullscreenChange]);

  // Show skeleton if mediaView is not ready or if there are no valid medias
  const hasValidVideos = mediaView?.videos && mediaView.videos.length > 0 && mediaView.videos.some((video) => video.url);
  const isPlayerReady = mediaView && hasValidVideos && medias.length > 0;

  if (!isPlayerReady) {
    return <Skeleton className={cn('aspect-[1.554] w-full', className)} />;
  }

  return (
    <VideoPlayer
      key={mediaView?.mediaInfo?.id || medias[0]?.src}
      className={cn({ 'z-0 aspect-[1.554]': !isCobliCamMulti }, className)}
      medias={medias}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      isFavorite={hideControls ? undefined : isFavorite}
      onPrevious={goToPrevious}
      onNext={goToNext}
      timelineMarker={isEvent ? EVENT_TRIGGER_SECONDS : undefined}
      onFavoriteToggle={hideControls ? undefined : onFavoriteToggle}
      shareDropdown={hideControls ? undefined : <MenuItemShareMedia media={mediaView?.mediaInfo} />}
      downloadDropdown={hideControls ? undefined : medias.length > 1 && mediaView ? <MenuItemDownloadMedia mediaView={mediaView} /> : undefined}
      onEnded={trackWatched}
      onPlay={trackPlayed}
      onDownloadFinished={trackDownloaded}
      onCaptureImage={onCaptureImage}
      onCaptureImageError={onCaptureImageError}
      canCaptureImage={canCaptureImage}
      crossOrigin={crossOrigin}
      isLoading={isLoading && showLoading}
      isMultiInteraction={isCobliCamMulti}
      downloadable={!hideControls}
      muted
      autoPlay={autoPlay}
      streamType={streamType}
      liveLabel={liveLabel}
    />
  );
};

export default CobliCamPlayer;
